import { html, nothing, type TemplateResult } from "lit";
import type { ChatState } from "../controllers/chat.ts";
import { CHAT_STALE_IDLE_MS, isChatStale } from "./chat-sync-staleness.ts";

/**
 * Renders the small staleness pill that appears in the chat composer
 * footer (next to Attach/Palace/Draft) when the transcript is non-fresh.
 * Returns `nothing` when the verdict is `fresh`, so the call site is a
 * no-op when the operator's view is reliable.
 *
 * Driven by `isChatStale` (pure) + a 1s tick on the host. Re-evaluating
 * on each render would be wasteful (the verdict is monotone between
 * events), so the host only invokes `requestUpdate` when staleness is
 * visible.
 *
 * Note: the visible count (e.g. "5s") lives inside the label text. The
 * previous implementation also rendered a separate `.chat-sync-pill__age`
 * span with the same value, which caused a duplicate count to leak out
 * to the right of the pill whenever the label already included the
 * duration (Awaiting reply, Syncing, Last sync … ago). Keep the count
 * in a single place — the label.
 */
export function renderChatSyncPill(
  state: Pick<
    ChatState,
    "chatSync" | "chatRunId" | "chatSending" | "chatStreamStartedAt" | "connected"
  >,
  nowMs: number,
  thresholds?: { activeMs?: number; idleMs?: number },
): TemplateResult | typeof nothing {
  const verdict = isChatStale(state, nowMs, thresholds);
  if (verdict.kind === "fresh") {
    return nothing;
  }
  const ageSec = Math.round(verdict.ageMs / 1_000);
  const label =
    verdict.kind === "stale" && verdict.reason === "disconnected"
      ? "Disconnected"
      : verdict.kind === "stale" && verdict.reason === "no-recent-event"
        ? `Awaiting reply · ${Math.max(1, ageSec)}s`
        : verdict.kind === "stale" && verdict.reason === "no-recent-sync"
          ? `Last sync ${Math.max(1, ageSec)}s ago`
          : `Syncing · ${Math.max(1, ageSec)}s`;
  const severity =
    verdict.kind === "stale" ? (verdict.reason === "disconnected" ? "danger" : "warn") : "info";
  const title =
    verdict.kind === "stale"
      ? `Chat staleness: ${verdict.reason}`
      : verdict.kind === "syncing"
        ? `Chat syncing (${ageSec}s since last paint)`
        : "Chat in sync";
  return html`<span
    class="chat-sync-pill chat-sync-pill--${verdict.kind} chat-sync-pill--${severity}"
    role="status"
    aria-live="polite"
    title=${title}
    data-stale=${verdict.kind === "stale" ? verdict.reason : verdict.kind}
  >
    <span class="chat-sync-pill__dot" aria-hidden="true"></span>
    <span class="chat-sync-pill__label">${label}</span>
  </span>`;
}

/** Re-exported so the test file does not need a second import. */
export { CHAT_STALE_IDLE_MS };
